import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import chatService from '../services/chatService';
import { useChatTurn } from '../features/chatLoop/useChatTurn';
import { restoreChatAfterReload } from '../features/chatLoop/chatAcrossReload';
import ChatConfirmLayer from '../components/chat/ChatConfirmLayer';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  // Persistent chat state — with the conversation an action's page reload
  // kept, if this load follows one (features/chatLoop/chatAcrossReload.js).
  const [restored] = useState(restoreChatAfterReload);
  const [messages, setMessages] = useState(restored.messages);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isChatActive, setIsChatActive] = useState(
    restored.messages.length > 0
  );

  // Clean context structure
  const [conversationContext, setConversationContext] = useState({
    // Current Location (single source of truth)
    location: {
      projectId: null,
      projectName: null,
      structureId: null,
      structureName: null,
      roomId: null,
      roomName: null,
      path: '/',
    },

    // Navigation History
    navigation: {
      history: [],
      lastProject: { id: null, name: null },
      lastStructure: { id: null, name: null },
    },

    // Cached Data (mirrors visible UI)
    cache: {
      projects: [],
      structures: [],
      rooms: [],
      checkpoints: [],
    },

    // UI State
    ui: {
      availableActions: [],
      viewMode: null, // 'orbit', 'firstPerson', 'floorplan', 'elevations' when viewing structure
    },

    // User Info
    user: {
      id: null,
      isAdmin: false,
    },
  });

  // Track navigation history for context
  const navigationHistoryRef = useRef([]);

  // Add a message to the chat
  const addMessage = useCallback((message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  // Clear the conversation — not where the user is. The location and the
  // cached rooms are written back only when the viewer's structure changes,
  // so clearing them left the model with no scan until a reload.
  const clearMessages = useCallback(() => {
    setMessages([]);
    setConversationContext((prev) => ({
      ...prev,
      navigation: {
        history: [],
        lastProject: { id: null, name: null },
        lastStructure: { id: null, name: null },
      },
    }));
    chatService.resetConversation();
    setIsChatActive(false);
    setInputValue('');
  }, []);

  // Update context from tool results
  const updateContext = useCallback((updates) => {
    setConversationContext((prev) => {
      const newContext = { ...prev };

      // Deep merge for nested objects
      Object.keys(updates).forEach((key) => {
        if (
          typeof updates[key] === 'object' &&
          updates[key] !== null &&
          !Array.isArray(updates[key])
        ) {
          // Special handling for cache object - preserve existing values not in update
          if (key === 'cache') {
            newContext[key] = {
              ...prev[key],
              ...updates[key],
              // Explicitly preserve structures if not provided in update
              structures:
                updates[key].structures !== undefined
                  ? updates[key].structures
                  : prev[key]?.structures || [],
              // Preserve projects if not provided
              projects:
                updates[key].projects !== undefined
                  ? updates[key].projects
                  : prev[key]?.projects || [],
            };
          } else {
            // For other objects, merge deeply
            newContext[key] = {
              ...prev[key],
              ...updates[key],
            };
          }
        } else {
          // For primitives and arrays, replace directly
          newContext[key] = updates[key];
        }
      });

      return newContext;
    });
  }, []);

  // Track navigation
  const trackNavigation = useCallback((path, title) => {
    setConversationContext((prev) => {
      const navItem = {
        path,
        title: title || document.title,
        timestamp: Date.now(),
        // Include current location snapshot
        projectId: prev.location.projectId,
        projectName: prev.location.projectName,
        structureId: prev.location.structureId,
        structureName: prev.location.structureName,
      };

      // Add to history and keep last 10
      const newHistory = [...prev.navigation.history, navItem].slice(-10);

      // Update last visited items
      const newLastProject = prev.location.projectId
        ? { id: prev.location.projectId, name: prev.location.projectName }
        : prev.navigation.lastProject;
      const newLastStructure = prev.location.structureId
        ? { id: prev.location.structureId, name: prev.location.structureName }
        : prev.navigation.lastStructure;

      return {
        ...prev,
        location: {
          ...prev.location,
          path,
        },
        navigation: {
          history: newHistory,
          lastProject: newLastProject,
          lastStructure: newLastStructure,
        },
      };
    });
  }, []);

  // Get the last known location of a specific type
  const getLastKnownLocation = useCallback(
    (type) => {
      if (type === 'project') {
        return conversationContext.navigation.lastProject;
      }
      if (type === 'structure') {
        return conversationContext.navigation.lastStructure;
      }
      return null;
    },
    [conversationContext.navigation]
  );

  // A turn the browser runs with the app's own actions (admins, for now).
  const browserTurn = useChatTurn({
    messages,
    addMessage,
    setIsLoading,
    setProgress,
    conversationContext,
    restoredConversation: restored.conversation,
  });

  const value = {
    // State
    messages,
    isLoading,
    currentTaskId,
    progress,
    inputValue,
    isChatActive,
    conversationContext,

    // Setters
    setMessages,
    setIsLoading,
    setCurrentTaskId,
    setProgress,
    setInputValue,
    setIsChatActive,

    // Actions
    addMessage,
    clearMessages,
    updateContext,
    trackNavigation,
    getLastKnownLocation,

    // The browser-run turn: browserLoop, sendBrowserTurn, pendingConfirm,
    // answerConfirm, stopTurn, turnRunning
    ...browserTurn,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
      <ChatConfirmLayer
        pending={browserTurn.pendingConfirm}
        onAnswer={browserTurn.answerConfirm}
      />
    </ChatContext.Provider>
  );
};
